import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'

import { BrowserRouter } from 'react-router-dom'
import { AppProvider } from './context/AppContext'
import { ClerkProvider } from '@clerk/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from './api/client'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Trace 10% of transactions; full errors always sent.
    tracesSampleRate: 0.1,
    // Session replay only kicks in when an error fires.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // Propagate trace headers to our backend so frontend ↔ backend traces stitch together.
    tracePropagationTargets: [
      /^\/v1\//,
      /^https:\/\/swiss-parliament-api\.innoautomation\.ch/,
      /^https:\/\/.*\.vercel\.app/,
    ],
    enabled: import.meta.env.PROD,
  })
}

// Phase 4.1.3 — central React Query client. Defaults align with the previous
// hand-rolled fetch behaviour: list endpoints are stable for ~1 min and
// refetch-on-focus is on so users coming back to a tab see fresh counts
// without a page reload. Retry policy (was `false`): transient failures —
// 5xx and network errors (ApiError.status 0) — are worth retrying twice with
// backoff; client errors (4xx) never are, since the request itself is wrong.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime:    5 * 60_000,
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        const status = error instanceof ApiError ? error.status : 0;
        return status === 0 || status >= 500;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
      refetchOnWindowFocus: true,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProvider>
            <App />
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
)
